import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PreRegistrationForm from '../components/PreRegistrationForm';
import { useAnimals } from '../hooks/useAnimals';
import './PremiumMilk.css';

const PremiumMilk = () => {
  const [testReport, setTestReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    fetch('/api/latest-test.json')
      .then(res => res.json())
      .then(data => setTestReport(data))
      .catch(err => console.error('Error fetching test report:', err));
  }, []);

  // Real testimonials will be added from actual customer feedback
  const testimonials = [
    {
      name: "Customer Testimonial",
      role: "Premium Milk Subscriber",
      text: "PharmWealth milk has transformed our family's health. The quality and traceability give us complete confidence in what we're consuming.",
      location: "India"
    }
  ];

  // Fetch real animals from Firestore
  const { animals, loading: animalsLoading, error: animalsError } = useAnimals(12);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Animated numbers effect
  useEffect(() => {
    const animateNumbers = () => {
      const statNumbers = document.querySelectorAll('.stat-number');
      statNumbers.forEach((stat) => {
        const target = parseInt(stat.getAttribute('data-target'));
        const duration = 2000;
        const increment = target / (duration / 16);
        let current = 0;
        
        const timer = setInterval(() => {
          current += increment;
          if (current >= target) {
            stat.textContent = target + '%';
            clearInterval(timer);
          } else {
            stat.textContent = Math.floor(current) + '%';
          }
        }, 16);
      });
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateNumbers();
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    const nutritionSection = document.querySelector('.nutrition-numbers');
    if (nutritionSection) {
      observer.observe(nutritionSection);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="premium-milk-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background parallax-container">
          <div className="parallax-bg" style={{
            backgroundImage: 'linear-gradient(to bottom, rgba(11, 19, 43, 0.6), rgba(11, 19, 43, 0.4), transparent), url(https://images.unsplash.com/photo-1560493676-04071c5f467b?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        <div className="container hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-headline"
          >
            Ethical, Antibiotic-Free, Farm-Traceable Milk for Urban Families
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-subheadline"
          >
            Hand-milked, lab-tested premium milk from named, stress-free animals. Limited quantities available — pre-register now to join our priority waiting list.
          </motion.p>
          <motion.ul
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hero-proof-bullets"
          >
            <li>Zero detectable antibiotic residue (third-party lab tested for every batch)</li>
            <li>Self-grown, chemical-free fodder for higher natural CLA and Omega-3s</li>
            <li>QR code traceability from animal group to bottle, every single day</li>
          </motion.ul>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="hero-ctas"
          >
            <a href="#pre-registration-form" className="btn btn-primary">Join the Priority List</a>
            <button onClick={() => setShowModal(true)} className="btn btn-outline">
              See Today's Lab Report
            </button>
          </motion.div>
        </div>
      </section>

      {/* Humane Section */}
      <section className="section humane-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            Humane, Relationship-Based Dairying
          </motion.h2>
          <div className="gold-line"></div>
          <motion.p
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-description"
          >
            On our farm, every animal has a name and a calm daily rhythm. No anonymous herd numbers or mechanical stress — just trusted caretakers who hand-milk the same cows they feed. We never cull, abandon, or sell off animals. Female and male calves are raised with dignity, and elders live in our dedicated gau-shala.
          </motion.p>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="humane-cta"
          >
            <button className="btn btn-secondary">Meet Our Cows →</button>
          </motion.div>
          {animalsLoading ? (
            <div className="cow-carousel-loading">
              <p>Loading our animals...</p>
            </div>
          ) : animalsError ? (
            <div className="cow-carousel-error">
              <p>{animalsError}</p>
            </div>
          ) : animals.length === 0 ? (
            <div className="cow-carousel-empty">
              <p>No animals found. Check back soon!</p>
            </div>
          ) : (
            <div className="cow-carousel">
              {animals.map((animal, index) => (
                <motion.div
                  key={animal.id}
                  initial="initial"
                  whileInView="animate"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="cow-portrait card"
                >
                  <div className="cow-image">
                    {animal.photoUrl ? (
                      <img 
                        src={animal.photoUrl} 
                        alt={animal.name}
                        loading="lazy"
                        onError={(e) => {
                          // Fallback to emoji if image fails to load
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '🐄';
                        }}
                      />
                    ) : (
                      <span className="cow-emoji">🐄</span>
                    )}
                  </div>
                  <h4>{animal.name}</h4>
                  {animal.breed && animal.breed !== 'N/A' && (
                    <p className="cow-breed">{animal.breed}</p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Zero Antibiotics Section */}
      <section className="section zero-antibiotics-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            Zero Antibiotics, No Hormones — Ever
          </motion.h2>
          <div className="gold-line"></div>
          <motion.p
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-description"
          >
            Across India, misuse of antibiotics in dairy animals has become a public-health issue. PharmWealth enforces a <strong>Zero Preventive Antibiotic Protocol</strong>:
          </motion.p>
          <motion.ul
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="antibiotics-list"
          >
            <li>No growth promoters, rBST, or prophylactic antibiotics.</li>
            <li>Sick animals are quarantined, treated under vet care, and milk withheld far beyond mandated withdrawal periods.</li>
            <li>Every batch is independently lab-tested for multi-antibiotic panels, with QR-linked reports visible to buyers.</li>
          </motion.ul>
        </div>
      </section>

      {/* From Soil to Glass Section */}
      <section className="section soil-to-glass-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            From Soil to Glass — Closed-Loop Purity
          </motion.h2>
          <div className="gold-line"></div>
          <motion.p
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-description"
          >
            Clean milk begins long before milking. Our self-grown green fodder—berseem, Napier, maize, and Azolla—is nourished with composted manure, not chemical fertilisers.<br />
            Crop waste becomes biogas and organic compost, closing the loop between soil, feed, and energy.
          </motion.p>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="closed-loop-infographic"
          >
            <div className="loop-step">🌱 Soil</div>
            <div className="loop-arrow">→</div>
            <div className="loop-step">🌾 Fodder</div>
            <div className="loop-arrow">→</div>
            <div className="loop-step">🐄 Cow</div>
            <div className="loop-arrow">→</div>
            <div className="loop-step">🥛 Milk</div>
            <div className="loop-arrow">→</div>
            <div className="loop-step">♻️ Compost</div>
          </motion.div>
        </div>
      </section>

      {/* Nutritionally Superior Section */}
      <section className="section nutrition-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            Nutritionally Superior by Design
          </motion.h2>
          <div className="gold-line"></div>
          <motion.p
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-description"
          >
            Scientific studies consistently show that organic, forage-based milk contains higher Omega-3 and CLA levels and healthier Omega-6:Omega-3 ratios.<br />
            By focusing on shade, unrestricted movement, and calm hand-milking, PharmWealth's milk naturally aligns with this superior profile—rich in good fats, better solids, and free from stress hormones.
          </motion.p>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="nutrition-numbers"
          >
            <div className="nutrition-stat">
              <div className="stat-number" data-target="40">0</div>
              <div className="stat-label">CLA +40%</div>
            </div>
            <div className="nutrition-stat">
              <div className="stat-number" data-target="60">0</div>
              <div className="stat-label">Omega-3 +60%</div>
            </div>
          </motion.div>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="science-table-wrapper"
          >
            <table className="science-table">
              <thead>
                <tr>
                  <th>Nutrient</th>
                  <th>Industrial</th>
                  <th>PharmWealth</th>
                  <th>Advantage</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['CLA', 'Baseline', '+40–50%', 'Functional Immunity'],
                  ['Omega-3', 'Low', '+60%', 'Heart Health'],
                  ['ω-6/ω-3', '5.77', '2.28', 'Balanced Ratio'],
                  ['SCC', '>4M', '<1.5M', 'Low Stress'],
                  ['Antibiotics', '20–35% fail', '0', 'Verified Purity']
                ].map((row, i) => (
                  <tr key={i}>
                    <td><strong>{row[0]}</strong></td>
                    <td>{row[1]}</td>
                    <td>{row[2]}</td>
                    <td>{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
          <div className="science-cta">
            <button onClick={() => setShowModal(true)} className="btn btn-primary">
              See Lab Report
            </button>
          </div>
        </div>
      </section>


      {/* Sustainability Section */}
      <section className="section sustainability-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            Sustainability
          </motion.h2>
          <div className="sustainability-grid">
            {[
              { icon: '⚡', value: '70%', label: 'Energy from biogas' },
              { icon: '🌾', value: '100%', label: 'Self-grown feed' },
              { icon: '♻️', value: '95%', label: 'Waste recycled' },
              { icon: '💧', value: '30%', label: 'Water saved' }
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="sustainability-item card"
              >
                <div className="sustainability-icon">{item.icon}</div>
                <h3>{item.value}</h3>
                <p>{item.label}</p>
              </motion.div>
            ))}
          </div>
          <motion.p
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="sustainability-tagline"
          >
            Good for you. Good for them. Good for Earth.
          </motion.p>
        </div>
      </section>

      {/* Traceability Section */}
      <section className="section traceability-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            Traceability You Can Actually See
          </motion.h2>
          <div className="gold-line"></div>
          <motion.p
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-description"
          >
            Every PharmWealth bottle carries a scannable QR code revealing:
          </motion.p>
          <motion.ul
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="traceability-list"
          >
            <li>Milking date and time window</li>
            <li>Animal group and housing area</li>
            <li>Latest antibiotic-residue panel results</li>
            <li>Fat %, SNF %, and somatic-cell data</li>
          </motion.ul>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="traceability-visual"
          >
            <div className="qr-code-mock">📱 QR Code</div>
            <p className="trace-instructions">
              Scan the QR code on your PharmWealth milk bottle to view complete traceability information.
            </p>
            <div className="trace-input-section">
              <input
                type="text"
                id="batchIdInput"
                placeholder="Or enter Batch ID manually"
                className="batch-id-input"
              />
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  const batchId = document.getElementById('batchIdInput')?.value?.trim();
                  if (batchId) {
                    window.location.href = `/trace/${batchId}`;
                  } else {
                    alert('Please enter a Batch ID to trace your bottle.');
                  }
                }}
              >
                Trace My Bottle →
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* For Families Section */}
      <section className="section families-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            For Families Who Refuse to Compromise
          </motion.h2>
          <div className="gold-line"></div>
          <motion.p
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-description"
          >
            Urban families are redefining "premium." It's not about fancy packaging—it's about trust.<br />
            PharmWealth milk is for parents, professionals, and ethical consumers who'd rather buy less but buy right.<br />
            Once you taste transparent purity, there's no going back to anonymous milk.
          </motion.p>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="families-cta"
          >
            <a href="#pre-registration-form" className="btn btn-primary">Pre-Register Now →</a>
          </motion.div>
        </div>
      </section>

      {/* How Subscriptions Work Section */}
      <section className="section subscriptions-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            How PharmWealth Subscriptions Work
          </motion.h2>
          <div className="gold-line"></div>
          <div className="subscription-steps">
            {[
              { number: '1️⃣', title: 'Choose Your City & Route', desc: 'Select your delivery area' },
              { number: '2️⃣', title: 'Set Your Daily Quantity', desc: 'Customize your order' },
              { number: '3️⃣', title: 'Lock In Your Slot', desc: 'Secure your delivery time' },
              { number: '4️⃣', title: 'Track & Taste the Difference', desc: 'Enjoy premium quality' }
            ].map((step, index) => (
              <motion.div
                key={step.title}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.15 }}
                className="subscription-step-card"
              >
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ESG Section */}
      <section className="section esg-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            ESG – Milk That Ages Well on Your Balance Sheet
          </motion.h2>
          <div className="gold-line"></div>
          <motion.p
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-description"
          >
            PharmWealth aligns with evolving ESG and sustainable-investing standards through:
          </motion.p>
          <motion.ul
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="esg-list"
          >
            <li>Measurable biogas output & emission reduction</li>
            <li>Reduced chemical inputs, self-grown feed</li>
            <li>Transparent labour & welfare practices</li>
            <li>A documented no-kill model</li>
          </motion.ul>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="esg-cta"
          >
            <Link to="/investors" className="btn btn-secondary">Request ESG Deck</Link>
          </motion.div>
        </div>
      </section>

      {/* Limited Availability Section */}
      <section className="section limited-availability-section">
        <div className="container">
          <motion.h2
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-title"
          >
            Limited Quantities, Unlimited Quality
          </motion.h2>
          <div className="gold-line"></div>
          <motion.p
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="section-description"
          >
            Our premium milk varieties are produced in small batches with meticulous care. Due to limited availability, we operate on a pre-registration basis. Join our priority waiting list to be notified when your preferred milk type becomes available.
          </motion.p>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="availability-cta"
          >
            <a href="#pre-registration-form" className="btn btn-primary">Join the Priority List →</a>
          </motion.div>
        </div>
      </section>

      {/* Pre-Registration Form Section */}
      <section id="pre-registration-form" className="section pre-registration-section">
        <div className="container">
          <PreRegistrationForm />
        </div>
      </section>

      {/* Test Report Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>Latest Lab Test Report</h2>
            {testReport ? (
              <div className="test-report">
                <div className="test-report-header">
                  <p><strong>Date:</strong> {testReport.date}</p>
                  <p><strong>Lab:</strong> {testReport.lab}</p>
                </div>
                <div className="test-results">
                  <div className="test-result-item">
                    <strong>Antibiotics:</strong> {testReport.results.antibiotics.status} ({testReport.results.antibiotics.value})
                  </div>
                  <div className="test-result-item">
                    <strong>CLA:</strong> {testReport.results.cla.value} ({testReport.results.cla.advantage})
                  </div>
                  <div className="test-result-item">
                    <strong>Omega-3:</strong> {testReport.results.omega3.value} ({testReport.results.omega3.advantage})
                  </div>
                  <div className="test-result-item">
                    <strong>ω-6/ω-3 Ratio:</strong> {testReport.results.omega6_omega3_ratio.value}
                  </div>
                  <div className="test-result-item">
                    <strong>SCC:</strong> {testReport.results.scc.value}
                  </div>
                  <div className="test-result-item">
                    <strong>Fat:</strong> {testReport.results.fat}
                  </div>
                  <div className="test-result-item">
                    <strong>SNF:</strong> {testReport.results.snf}
                  </div>
                </div>
                <p className="test-certification">{testReport.certification}</p>
                <p className="test-notes">{testReport.notes}</p>
              </div>
            ) : (
              <p>Loading test report...</p>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PremiumMilk;

